/**
 * ChatPage - MarIA Health Care
 *
 * Implements the official Google ADK pattern for frontend integration.
 * Uses SSE (Server-Sent Events) via /run_sse endpoint instead of WebSocket.
 *
 * Based on:
 * - https://github.com/google/adk-samples/tree/main/python/agents/deep-search
 * - https://google.github.io/adk-docs/runtime/api-server/
 *
 * Flow:
 * 1. Create session via POST /apps/{app}/users/{user}/sessions/{session}
 * 2. Send messages via POST /run_sse with SSE streaming response
 * 3. Process SSE events to extract agent responses
 *
 * Voice Input (Issue #XX):
 * - Uses Web Speech API for speech-to-text
 * - Transcribed text is sent via existing /run_sse endpoint
 * - No backend changes required
 */
import { useRef, useState, useCallback, useEffect, lazy, Suspense } from "react";
import ReactMarkdown from "react-markdown";
import { Navbar } from "@/components/layout/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VoiceInput } from "@/components/VoiceInput";
import { useAuth } from "@/contexts/AuthContext";
import { logger } from "@/lib/logger";

// Avatar interactivo HeyGen — desactivado por defecto. Si VITE_ENABLE_AVATAR
// no es "true", el componente NI siquiera entra al bundle (lazy import nunca
// se evalúa). Esto evita que Vite tenga que resolver @heygen/streaming-avatar
// en producción, paquete deprecated cuya migración a @heygen/liveavatar-web-sdk
// queda pendiente para un MR separado con API key real.
const AVATAR_ENABLED = import.meta.env.VITE_ENABLE_AVATAR === "true";
const AvatarPresentation = AVATAR_ENABLED
    ? lazy(() => import("@/components/AvatarPresentation"))
    : null;
type AvatarHandle = { speak: (text: string) => Promise<void> };

// ADK Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";
const ADK_APP_NAME = "maria";

interface Message {
    id: string;
    type: "user" | "assistant";
    text: string;
}

/**
 * Extract text from ADK SSE event data
 * Follows the official ADK response format
 *
 * ADK sends multiple events per response:
 * 1. partial:true chunks → se CONCATENAN (streaming en vivo).
 * 2. evento final sin partial → respuesta COMPLETA. El after_model_callback del
 *    backend añade aquí la sección "Fuentes". Se devuelve con isFinal:true para
 *    que el bucle REEMPLACE el acumulado si extiende lo ya mostrado (no duplica).
 */
type SSEChunk = { text: string; isComplete: boolean; shouldSkip: boolean; isFinal: boolean; errorMessage?: string };

/**
 * Traduce los errores que ADK serializa en el stream a un mensaje legible.
 * - Eventos ADK con `errorCode`/`errorMessage` (camelCase): bloqueos de
 *   seguridad de Gemini (SAFETY, PROHIBITED_CONTENT...), MAX_TOKENS, etc.
 * - `{"error": ...}` emitido por el generador de /run_sse ante una excepción.
 */
function describeStreamError(code?: string, message?: string): string {
    if (code && /SAFETY|BLOCK|PROHIBITED|RECITATION|SPII/i.test(code)) {
        return "No puedo responder a esa consulta por las políticas de seguridad del asistente. Prueba a reformularla de otra manera.";
    }
    if (code && /MAX_TOKENS/i.test(code)) {
        return "La respuesta se ha cortado por longitud. Prueba a dividir la pregunta en partes más pequeñas.";
    }
    return `Lo siento, hubo un error al generar la respuesta${message ? `: ${message}` : ""}.`;
}

function extractTextFromSSE(data: string): SSEChunk {
    try {
        const parsed = JSON.parse(data);

        // Errores explícitos del stream: no son texto, pero deben verse en la burbuja.
        if (typeof parsed.error === "string" || (parsed.error && typeof parsed.error === "object")) {
            const detail = typeof parsed.error === "string" ? parsed.error : parsed.error?.message;
            return { text: "", isComplete: true, shouldSkip: true, isFinal: false, errorMessage: describeStreamError(undefined, detail) };
        }
        if (parsed.errorCode || parsed.errorMessage) {
            return {
                text: "",
                isComplete: true,
                shouldSkip: true,
                isFinal: false,
                errorMessage: describeStreamError(parsed.errorCode, parsed.errorMessage),
            };
        }

        // Evento final no-parcial: ADK envía la respuesta COMPLETA. Históricamente
        // se descartaba para evitar duplicar el texto que ya llegó en los chunks
        // partial:true. PERO el after_model_callback del backend añade la sección
        // "Fuentes" precisamente en este evento final (no parcial). Por eso ahora
        // NO lo descartamos: devolvemos su texto con isFinal:true para que el bucle
        // REEMPLACE el acumulado (no concatene) si trae contenido extra (las Fuentes).
        if (parsed.content?.parts && parsed.partial === undefined) {
            const finalTexts = parsed.content.parts
                .filter((part: { text?: string }) => part.text)
                .map((part: { text: string }) => part.text);
            return {
                text: finalTexts.join(""),
                isComplete: true,
                shouldSkip: finalTexts.length === 0,
                isFinal: true,
            };
        }

        // Check for content.parts[].text (standard ADK format)
        // Only process if partial:true (streaming chunks)
        if (parsed.content?.parts && parsed.partial === true) {
            const texts = parsed.content.parts
                .filter((part: { text?: string }) => part.text)
                .map((part: { text: string }) => part.text);

            if (texts.length > 0) {
                const isComplete = parsed.finishReason === "STOP";
                return { text: texts.join(""), isComplete, shouldSkip: false, isFinal: false };
            }
        }

        // Check for actions.stateDelta (final response marker) - skip to avoid duplication
        if (parsed.actions?.stateDelta?.final_response) {
            return { text: "", isComplete: true, shouldSkip: true, isFinal: false };
        }

        // Check for direct text field
        if (typeof parsed.text === "string") {
            return { text: parsed.text, isComplete: false, shouldSkip: false, isFinal: false };
        }

        return { text: "", isComplete: false, shouldSkip: false, isFinal: false };
    } catch {
        // Not JSON, might be raw text
        return { text: "", isComplete: false, shouldSkip: false, isFinal: false };
    }
}

export default function ChatPage() {
    const { user, getIdToken } = useAuth();
    const inputRef = useRef<HTMLInputElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const avatarRef = useRef<AvatarHandle>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    const [messages, setMessages] = useState<Message[]>([]);
    const [inputValue, setInputValue] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [sessionId, setSessionId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    // Voice input state
    const [interimTranscript, setInterimTranscript] = useState("");

    // Accumulated text for streaming responses
    const accumulatedTextRef = useRef("");

    /**
     * Generate unique message ID
     */
    const generateMessageId = () => `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    /**
     * Generate session ID (UUID v4 format)
     */
    const generateSessionId = () => {
        return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === "x" ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    };

    /**
     * Create ADK session for current user
     * Following official pattern: POST /apps/{app}/users/{user}/sessions/{session}
     */
    const createSession = useCallback(async (): Promise<string | null> => {
        if (!user?.firebaseUid) {
            logger.error("No Firebase user for session creation");
            return null;
        }

        const token = await getIdToken();
        if (!token) {
            logger.error("Failed to get Firebase token");
            return null;
        }

        const newSessionId = generateSessionId();
        const sessionUrl = `${API_BASE_URL}/apps/${ADK_APP_NAME}/users/${user.firebaseUid}/sessions/${newSessionId}`;

        try {
            const response = await fetch(sessionUrl, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({}),
            });

            if (!response.ok) {
                // Session might be auto-created by /run_sse, continue anyway
                logger.warn(`Session creation returned ${response.status}, continuing...`);
            }

            logger.info(`ADK session created: ${newSessionId}`);
            return newSessionId;
        } catch (err) {
            logger.error("Failed to create session:", err);
            return newSessionId; // Return ID anyway, /run_sse might create it
        }
    }, [user, getIdToken]);

    /**
     * Muestra un error del stream EN LA MISMA burbuja del asistente (no crea otra).
     * Si ya había texto parcial, lo conserva y añade el aviso debajo.
     */
    const showStreamError = (aiMessageId: string, errorMessage: string) => {
        const partial = accumulatedTextRef.current;
        const text = partial ? `${partial}\n\n_${errorMessage}_` : errorMessage;
        setMessages((prev) =>
            prev.map((msg) => (msg.id === aiMessageId ? { ...msg, text } : msg))
        );
    };

    /**
     * Process SSE stream from /run_sse
     * Following official Google ADK pattern
     */
    const processSSEStream = async (
        response: Response,
        aiMessageId: string
    ): Promise<void> => {
        const reader = response.body?.getReader();
        if (!reader) return;

        const decoder = new TextDecoder();
        let lineBuffer = "";
        let eventDataBuffer = "";
        accumulatedTextRef.current = "";

        try {
            while (true) {
                const { done, value } = await reader.read();

                if (value) {
                    lineBuffer += decoder.decode(value, { stream: true });
                }

                let eolIndex;
                while ((eolIndex = lineBuffer.indexOf("\n")) >= 0 || (done && lineBuffer.length > 0)) {
                    let line: string;
                    if (eolIndex >= 0) {
                        line = lineBuffer.substring(0, eolIndex);
                        lineBuffer = lineBuffer.substring(eolIndex + 1);
                    } else {
                        line = lineBuffer;
                        lineBuffer = "";
                    }

                    if (line.trim() === "") {
                        // Empty line: dispatch event
                        if (eventDataBuffer.length > 0) {
                            const jsonData = eventDataBuffer.endsWith("\n")
                                ? eventDataBuffer.slice(0, -1)
                                : eventDataBuffer;

                            const { text, shouldSkip, isFinal, errorMessage } = extractTextFromSSE(jsonData);
                            if (errorMessage) {
                                showStreamError(aiMessageId, errorMessage);
                            } else if (text && !shouldSkip) {
                                // Evento final no-parcial: REEMPLAZA el acumulado si trae
                                // más contenido (las "Fuentes" que añade el callback del
                                // backend). Si no extiende lo ya mostrado, se ignora para
                                // no duplicar. Los chunks partial:true se CONCATENAN.
                                if (isFinal) {
                                    if (text.length > accumulatedTextRef.current.length) {
                                        accumulatedTextRef.current = text;
                                    }
                                } else {
                                    accumulatedTextRef.current += text;
                                }
                                setMessages((prev) =>
                                    prev.map((msg) =>
                                        msg.id === aiMessageId
                                            ? { ...msg, text: accumulatedTextRef.current }
                                            : msg
                                    )
                                );
                            }
                            eventDataBuffer = "";
                        }
                    } else if (line.startsWith("data:")) {
                        eventDataBuffer += line.substring(5).trimStart() + "\n";
                    }
                    // Ignore comment lines starting with ':'
                }

                if (done) {
                    // Process any remaining data
                    if (eventDataBuffer.length > 0) {
                        const jsonData = eventDataBuffer.endsWith("\n")
                            ? eventDataBuffer.slice(0, -1)
                            : eventDataBuffer;
                        const { text, shouldSkip, isFinal, errorMessage } = extractTextFromSSE(jsonData);
                        if (errorMessage) {
                            showStreamError(aiMessageId, errorMessage);
                        } else if (text && !shouldSkip) {
                            // Mismo criterio que el bucle: el evento final reemplaza
                            // (trae las Fuentes); los chunks parciales concatenan.
                            if (isFinal) {
                                if (text.length > accumulatedTextRef.current.length) {
                                    accumulatedTextRef.current = text;
                                }
                            } else {
                                accumulatedTextRef.current += text;
                            }
                            setMessages((prev) =>
                                prev.map((msg) =>
                                    msg.id === aiMessageId
                                        ? { ...msg, text: accumulatedTextRef.current }
                                        : msg
                                )
                            );
                        }
                    }
                    break;
                }
            }
        } finally {
            reader.releaseLock();
        }

        // Trigger avatar to speak the final response
        if (accumulatedTextRef.current && avatarRef.current) {
            avatarRef.current.speak(accumulatedTextRef.current).catch(console.error);
        }
    };

    /**
     * Send message to MarIA agent via ADK /run_sse
     * Following official Google ADK pattern
     *
     * @param messageText - Optional text to send (used by voice input)
     *                      If not provided, uses inputValue state
     */
    const handleSend = useCallback(async (messageText?: string) => {
        const query = (messageText || inputValue).trim();
        if (!query || isLoading) return;

        if (!user?.firebaseUid) {
            setError("No estás autenticado. Por favor, recarga la página.");
            return;
        }

        setError(null);
        setIsLoading(true);
        setInputValue("");
        setInterimTranscript(""); // Clear any interim voice transcript

        // Cancel any pending request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        // Id de la burbuja del asistente, visible también en el catch para
        // actualizarla en lugar de añadir una segunda burbuja de error.
        let aiMessageId: string | null = null;

        try {
            // Ensure we have a session
            let currentSessionId = sessionId;
            if (!currentSessionId) {
                currentSessionId = await createSession();
                if (currentSessionId) {
                    setSessionId(currentSessionId);
                }
            }

            if (!currentSessionId) {
                throw new Error("No se pudo crear la sesión");
            }

            // Get fresh Firebase token
            const token = await getIdToken();
            if (!token) {
                throw new Error("No se pudo obtener el token de autenticación");
            }

            // Add user message to chat
            const userMessageId = generateMessageId();
            setMessages((prev) => [...prev, { id: userMessageId, type: "user", text: query }]);

            // Create AI message placeholder
            const newAiMessageId = generateMessageId();
            aiMessageId = newAiMessageId;
            setMessages((prev) => [...prev, { id: newAiMessageId, type: "assistant", text: "" }]);

            // Send message via ADK /run_sse (official pattern)
            const response = await fetch(`${API_BASE_URL}/run_sse`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    app_name: ADK_APP_NAME,
                    user_id: user.firebaseUid,
                    session_id: currentSessionId,
                    new_message: {
                        role: "user",
                        parts: [{ text: query }],
                    },
                    streaming: true,
                }),
                signal: abortControllerRef.current.signal,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Error del servidor: ${response.status} - ${errorText}`);
            }

            // Process SSE stream
            await processSSEStream(response, newAiMessageId);

        } catch (err) {
            if ((err as Error).name === "AbortError") {
                logger.info("Request aborted");
                return;
            }

            const errorMessage = err instanceof Error ? err.message : "Error desconocido";
            logger.error("Error sending message:", err);
            setError(errorMessage);

            // Actualizar la burbuja ya creada (si existe) en vez de dejarla vacía
            // en "Pensando…" y añadir otra. Si falló antes de crearla, se añade una.
            const errorText = `Lo siento, hubo un error: ${errorMessage}`;
            const failedId = aiMessageId;
            setMessages((prev) => {
                if (failedId && prev.some((m) => m.id === failedId)) {
                    return prev.map((m) =>
                        m.id === failedId
                            ? { ...m, text: m.text ? `${m.text}\n\n_${errorText}_` : errorText }
                            : m
                    );
                }
                return [...prev, { id: generateMessageId(), type: "assistant", text: errorText }];
            });
        } finally {
            setIsLoading(false);
        }
    }, [inputValue, isLoading, user, sessionId, createSession, getIdToken]);

    /**
     * Handle voice transcript - auto-send to MarIA
     */
    const handleVoiceTranscript = useCallback((transcript: string) => {
        if (transcript.trim()) {
            // Auto-send the voice message
            handleSend(transcript);
        }
    }, [handleSend]);

    /**
     * Handle interim voice results - show preview in input
     */
    const handleInterimResult = useCallback((interim: string) => {
        setInterimTranscript(interim);
    }, []);

    /**
     * Scroll to bottom when messages change
     */
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    /**
     * Initialize session on mount
     */
    useEffect(() => {
        if (user?.firebaseUid && !sessionId) {
            createSession().then((id) => {
                if (id) setSessionId(id);
            });
        }
    }, [user, sessionId, createSession]);

    /**
     * Cleanup on unmount
     */
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <Navbar />
            <div className="flex-1 flex flex-col max-w-3xl mx-auto w-full p-4 gap-3">
                {/* Avatar interactivo — solo se carga si VITE_ENABLE_AVATAR=true */}
                {AvatarPresentation && (
                    <Suspense fallback={null}>
                        <AvatarPresentation
                            ref={avatarRef}
                            welcomeMessage="¡Hola! Soy MarIA, ¿en qué puedo ayudarte hoy?"
                        />
                    </Suspense>
                )}

                {/* Chat Section */}
                <div className="flex-1 flex flex-col bg-white rounded-2xl shadow-sm border border-border overflow-hidden">
                    {/* Error Banner */}
                    {error && (
                        <div className="p-3 bg-red-50 border-b border-red-200 text-red-700 text-sm flex items-center justify-between gap-3">
                            <span>{error}</span>
                            <button
                                onClick={() => setError(null)}
                                className="text-red-500 hover:text-red-700 leading-none text-lg"
                                aria-label="Cerrar aviso de error"
                            >
                                ×
                            </button>
                        </div>
                    )}

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5">
                        {messages.length === 0 && (
                            <div className="text-center text-muted-foreground mt-8">
                                <p className="text-foreground">¡Hola! Soy MarIA, tu asistente de salud.</p>
                                <p className="text-sm mt-2">
                                    Escribe tu consulta o pulsa el micrófono para hablar.
                                </p>
                            </div>
                        )}
                        {messages.map((msg) => (
                            <div
                                key={msg.id}
                                className={`flex ${msg.type === "user" ? "justify-end" : "justify-start"}`}
                            >
                                <div
                                    className={
                                        msg.type === "user"
                                            ? "max-w-[80%] px-4 py-2.5 rounded-2xl rounded-br-md bg-brand-100 text-foreground border border-brand-200 shadow-sm"
                                            : "max-w-[85%] px-5 py-4 rounded-2xl rounded-bl-md bg-secondary text-secondary-foreground border border-brand-100 shadow-sm"
                                    }
                                >
                                    {msg.text ? (
                                        msg.type === "assistant" ? (
                                            <div className="text-[15px] leading-relaxed text-foreground space-y-3">
                                                <ReactMarkdown
                                                    components={{
                                                        h1: ({ children }) => (
                                                            <h2 className="text-lg sm:text-xl font-semibold text-primary mt-5 mb-2 first:mt-0">
                                                                {children}
                                                            </h2>
                                                        ),
                                                        h2: ({ children }) => (
                                                            <h3 className="text-base sm:text-lg font-semibold text-primary mt-5 mb-2 first:mt-0">
                                                                {children}
                                                            </h3>
                                                        ),
                                                        h3: ({ children }) => (
                                                            <h4 className="text-base font-semibold text-primary mt-4 mb-1.5 first:mt-0">
                                                                {children}
                                                            </h4>
                                                        ),
                                                        p: ({ children }) => {
                                                            const text =
                                                                Array.isArray(children) && children.length === 1
                                                                    ? children[0]
                                                                    : children;
                                                            const isSourcesLabel =
                                                                typeof text === "object" &&
                                                                text !== null &&
                                                                "props" in (text as object) &&
                                                                /^(?:Fuentes|Fuente|Sources?)\s*:?$/i.test(
                                                                    String(
                                                                        (text as { props?: { children?: unknown } }).props
                                                                            ?.children ?? ""
                                                                    )
                                                                );
                                                            if (isSourcesLabel) {
                                                                return (
                                                                    <p className="mt-6 pt-4 border-t border-brand-100 font-semibold text-primary">
                                                                        {children}
                                                                    </p>
                                                                );
                                                            }
                                                            return <p className="my-2">{children}</p>;
                                                        },
                                                        strong: ({ children }) => (
                                                            <strong className="font-semibold text-primary">
                                                                {children}
                                                            </strong>
                                                        ),
                                                        a: ({ children, href }) => (
                                                            <a
                                                                href={href}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-primary underline decoration-primary/40 hover:decoration-primary break-words"
                                                            >
                                                                {children}
                                                            </a>
                                                        ),
                                                        ul: ({ children }) => (
                                                            <ul className="list-disc pl-5 my-2 space-y-1">{children}</ul>
                                                        ),
                                                        ol: ({ children }) => (
                                                            <ol className="list-decimal pl-5 my-2 space-y-1">{children}</ol>
                                                        ),
                                                        li: ({ children }) => <li className="my-0.5">{children}</li>,
                                                    }}
                                                >
                                                    {msg.text}
                                                </ReactMarkdown>
                                            </div>
                                        ) : (
                                            <span className="text-[15px] leading-relaxed">{msg.text}</span>
                                        )
                                    ) : (
                                        <span className="inline-flex items-center text-muted-foreground">
                                            <span className="animate-pulse">Pensando</span>
                                            <span className="animate-bounce ml-1">…</span>
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input Area */}
                    <div className="p-4 border-t border-border bg-white">
                        {/* Interim transcript preview */}
                        {interimTranscript && (
                            <div className="mb-2 text-sm text-muted-foreground italic animate-pulse">
                                🎤 {interimTranscript}…
                            </div>
                        )}

                        <div className="flex gap-2 items-end">
                            {/* Voice Input Button */}
                            <VoiceInput
                                onTranscript={handleVoiceTranscript}
                                onInterimResult={handleInterimResult}
                                disabled={isLoading}
                                language="es-ES"
                                autoSend={true}
                            />

                            {/* Text Input */}
                            <Input
                                ref={inputRef}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
                                placeholder="Escribe tu mensaje…"
                                className="flex-1"
                                disabled={isLoading}
                                aria-label="Escribe tu mensaje"
                            />

                            {/* Send Button */}
                            <Button
                                onClick={() => handleSend()}
                                disabled={!inputValue.trim() || isLoading}
                                aria-label="Enviar mensaje"
                            >
                                {isLoading ? "…" : "Enviar"}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}